import React from 'react';
import dlv from 'dlv';
import TextField from '../../textfield';
import BaseDialog from './base';
import SelectList from '../../selectlist';
import Types from '../types';
import { bindEvent, exists } from '../../../../util';
import { editorForSpecialType } from '../fieldeditview/editors';

const typesOptions = [
	Types.string,
	Types.boolean,
	Types.number,
	Types.object,
	Types.array,
	Types.json
]

const prepareTemplatesForFields = (dataForPath, templates) => {
	if (!templates || !dataForPath) return {};
	return (Array.isArray(templates) ? templates : [templates]).filter(t => !exists(dataForPath[t.field])).reduce(
		(acc, { field, label, type, disabled = true, data }) => {
			acc[field] = { label, type, disabled, data };
			return acc;
		}, {}
	);
}

const getSpecialEditorForType = (templateForFieldType, defaults) => {
	const component = editorForSpecialType(dlv(templateForFieldType, 'type', defaults.fieldType));
	const value = dlv(templateForFieldType, 'data', defaults.fieldValue);
	const disabled = dlv(templateForFieldType, 'disabled');

	return component && {
		component,
		props: {
			value,
			...exists(disabled) && { disabled }
		}
	};
}

const AddFieldDialog = React.memo(
	({ onClose, findDataForPath, ...props }) => {
		const { fieldActionRequest } = props;
		const parentType = fieldActionRequest.type;

		const dataForPath = findDataForPath(fieldActionRequest.path);
		const templatesForFields = prepareTemplatesForFields(dataForPath, dlv(fieldActionRequest, 'args.fieldValue'));
		const allowAddCustomField = dlv(fieldActionRequest, 'args.allowAddCustomField');
		const fieldTypeOptions = [
			...Object.keys(templatesForFields).map(field => ({
				value: field,
				label: templatesForFields[field].label
			}))
		];

		if (allowAddCustomField) {
			fieldTypeOptions.unshift(...typesOptions.map(t => ({ value: t, label: t })));
		}

		const [fieldName, setFieldName] = React.useState();
		const [fieldType, setFieldType] = React.useState();
		const [fieldValue, setFieldValue] = React.useState();

		const handleClose = (confirm) => onClose && onClose(confirm && { fieldName, fieldType, fieldValue });

		React.useEffect(() => {
			const templateForFieldType = templatesForFields[fieldType];
			const fType = templateForFieldType ? templateForFieldType.data : undefined;

			/**
			 * If `templateForFieldType` is available, then we've a template data for selected `fieldType` (From the `fieldType` dropdown.). In this case, we keep `fieldName` same as `fieldType`.
			 * If `templatesForFields[fieldName]` is available, it means we had previously set `fieldName` equals to `fieldType`, so we need to reset `fieldName`.
			 * Otherwise, use `fieldName` as it is.
			 */
			const fName = templateForFieldType ? fieldType : templatesForFields[fieldName] ? undefined : fieldName;

			setFieldName(fName);
			setFieldValue(fType);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [fieldType]);

		if (fieldTypeOptions.length === 0) {
			return <EmptyAddDialog />
		} else if(parentType === Types.array) {
			return (
				<AddFieldDialogArray
					{...props}
					onClose={handleClose}
					fieldTypeOptions={fieldTypeOptions}
					fieldType={fieldType}
					setFieldType={setFieldType}
					fieldValue={fieldValue}
					setFieldValue={setFieldValue}
					templateForFieldType={templatesForFields[fieldType]}
				/>
			)
		} else {
			return (
				<AddFieldDialogObject
					{...props}
					onClose={handleClose}
					fieldTypeOptions={fieldTypeOptions}
					fieldName={fieldName}
					setFieldName={setFieldName}
					fieldType={fieldType}
					setFieldType={setFieldType}
					fieldValue={fieldValue}
					setFieldValue={setFieldValue}
					templateForFieldType={templatesForFields[fieldType]}
				/>
			)
		}
	}
);

export default AddFieldDialog;

const EmptyAddDialog = React.memo((props) => null); // TO DO: Write Code =)

const AddFieldDialogArray = React.memo(
	({ onClose, fieldTypeOptions, fieldType, setFieldType, fieldValue, setFieldValue, validateFieldPath, templateForFieldType, ...props }) => {
		const SpecialTypeEditor = getSpecialEditorForType(templateForFieldType, { fieldType, fieldValue });

		return (
			<BaseDialog
				{...props}
				onClose={onClose}
				title="Add List Item"
				confirmBtnTxt="Add"
				>
				<SelectList label="Data Type" options={fieldTypeOptions} onChange={bindEvent(setFieldType)}/>
				{SpecialTypeEditor && (
					<SpecialTypeEditor.component {...SpecialTypeEditor.props} onChange={setFieldValue} />
				)}
			</BaseDialog>
		);
	}
);

const AddFieldDialogObject = React.memo(
	({ onClose, fieldTypeOptions, fieldType, setFieldType, fieldValue, setFieldValue, fieldName, setFieldName, validateFieldPath, templateForFieldType, ...props }) => {
		const [validation, setFieldNameValidation] = React.useState({});

		React.useEffect(() => {
			setFieldNameValidation(validateFieldPath(fieldName));
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [fieldName, fieldType]);

		const SpecialTypeEditor = getSpecialEditorForType(templateForFieldType, { fieldType, fieldValue });

		return (
			<BaseDialog 
				{...props}
				onClose={onClose}
				title="Add Field"  
				confirmBtnTxt="Add"
				disableConfirm={validation.passed !== true} 
				>
				{!templateForFieldType && (
					<TextField
						label="Field Name:"
						autoFocus={true}
						onChange={bindEvent(setFieldName)}
						{...validation.passed === false && { error: true, helperText: validation.msg }}
						/>
				)}
				<SelectList label="Field Type" options={fieldTypeOptions} onChange={bindEvent(setFieldType)}/>
				{SpecialTypeEditor && (
					<SpecialTypeEditor.component {...SpecialTypeEditor.props} onChange={setFieldValue} />
				)}
			</BaseDialog>
		);
	}
);